import { useCallback, useEffect, useRef, useState } from 'react';
import type { Point, Tool, Stroke } from '@blackboard/shared';
import type { WsClient, WsEvent, WsStatus } from './WsClient';
import { useBoard } from '../state/board';
import { usePresence } from '../state/presence';
import { useUndo } from '../state/undo';
import { invalidateTiles } from './loadTiles';
import { toastPresenceJoin, toastPresenceLeave, eraseAndNotify, toastWsError } from './wsToasts';
import { markFresh } from '../canvas/freshnessTracker';
import { displayName } from '../utils/name';

interface InProgressStroke {
  remote_id: string;
  user_id: string;
  tool: Tool;
  color: string;
  sizePx: number;
  sizeIdx: number;
  points: Point[];
  isSelf: boolean;
  local_id?: number;
  pendingBatch: Point[];
}

export interface LiveRender {
  tool: Tool;
  color: string;
  sizePx: number;
  points: Point[];
}

function eventToStroke(e: {
  stroke_id: number;
  user_id: string;
  tool: string;
  color: string;
  size: number;
  bbox: [number, number, number, number];
  points: [number, number, number][];
}): Stroke {
  return {
    id: e.stroke_id,
    board_id: 0,
    user_id: e.user_id,
    tool: e.tool as Tool,
    color: e.color,
    size: e.size,
    points: e.points.map((p) => ({ x: p[0], y: p[1], pressure: p[2] })),
    bbox: { x1: e.bbox[0], y1: e.bbox[1], x2: e.bbox[2], y2: e.bbox[3] },
    created_at: new Date().toISOString(),
    erased_at: null,
    erased_by: null,
  };
}

/**
 * v2 提交式绘制管线：
 *  - 自己的笔画只进本地 ref（RAF 直读渲染，零 React 渲染、零网络流量）
 *  - 一笔画完才用 WS_FRAME_STROKE_COMMIT_FULL 整笔上行
 *  - 远端笔画经 strokes:add 批量事件到达（服务端 50ms 合帧）
 *  - 旧版流式事件（stroke:begin/append/commit）保留接收，兼容未刷新的旧客户端
 */
export function useDrawing(
  client: WsClient | null,
  selfUserId: string | null,
  sizePxMap: (idx: number, tool: Tool) => number,
  onReopen?: () => void,
  onStatus?: (s: WsStatus) => void
) {
  const liveRef = useRef<Map<string, InProgressStroke>>(new Map());
  const [liveCount, setLiveCount] = useState(0);
  const localIdRef = useRef(0);

  // Undo 动作栈（我画的笔画 + 我发起的擦除事件）在全局 store，
  // 管理员 REST 区域擦除也往里推。
  const undoCount = useUndo((s) => s.stack.length);

  const { addStroke, removeStroke } = useBoard();
  const { setUsers, addUser, removeUser, setCursor } = usePresence();

  const syncLiveCount = useCallback(() => {
    setLiveCount(liveRef.current.size);
  }, []);

  const handleEvent = useCallback(
    (e: WsEvent) => {
      switch (e.t) {
        case 'hello':
          setUsers(e.online);
          break;
        case 'presence:join': {
          const isNew = !usePresence.getState().users.has(e.user.user_id);
          addUser(e.user);
          if (isNew) toastPresenceJoin(selfUserId, e.user);
          break;
        }
        case 'presence:leave':
          removeUser(e.user_id);
          toastPresenceLeave(selfUserId, e.user_id);
          break;
        case 'cursor': {
          const user = usePresence.getState().users.get(e.user_id);
          if (user) {
            setCursor({
              user_id: e.user_id,
              name: displayName(user),
              color: user.color,
              x: e.x,
              y: e.y,
              ts: Date.now(),
            });
          }
          break;
        }
        case 'strokes:add': {
          const idx = useBoard.getState().index;
          for (const s of e.strokes) {
            if (idx.getById(s.stroke_id)) continue; // dedupe (reconnect echo)
            const stroke = eventToStroke(s);
            addStroke(stroke);
            if (s.user_id === selfUserId) useUndo.getState().push({ kind: 'stroke', id: s.stroke_id });
            else markFresh(s.stroke_id);
          }
          break;
        }
        case 'strokes:restore': {
          // 撤销擦除 / 管理员回滚：恢复的笔画带全量数据重新入索引。
          // 同步失效覆盖到的瓦片缓存，平移回来才能重新拉到这些笔画。
          const idx = useBoard.getState().index;
          let bx1 = Infinity, by1 = Infinity, bx2 = -Infinity, by2 = -Infinity;
          for (const s of e.strokes) {
            if (!idx.getById(s.stroke_id)) {
              addStroke(eventToStroke(s));
              if (s.user_id === selfUserId) useUndo.getState().push({ kind: 'stroke', id: s.stroke_id });
              else markFresh(s.stroke_id);
            }
            bx1 = Math.min(bx1, s.bbox[0]); by1 = Math.min(by1, s.bbox[1]);
            bx2 = Math.max(bx2, s.bbox[2]); by2 = Math.max(by2, s.bbox[3]);
          }
          if (bx1 !== Infinity) invalidateTiles({ x1: bx1, y1: by1, x2: bx2, y2: by2 });
          break;
        }
        // ---- legacy (v1 streaming) events: only other, not-yet-refreshed clients emit these ----
        case 'stroke:begin': {
          const existing = liveRef.current.get(e.remote_id);
          if (existing?.isSelf) break;
          liveRef.current.set(e.remote_id, {
            remote_id: e.remote_id,
            user_id: e.user_id,
            tool: e.tool,
            color: e.color,
            sizePx: sizePxMap(e.size, e.tool),
            sizeIdx: e.size,
            points: [{ x: e.p0[0], y: e.p0[1], pressure: e.p0[2] }],
            isSelf: false,
            pendingBatch: [],
          });
          syncLiveCount();
          break;
        }
        case 'stroke:append': {
          const cur = liveRef.current.get(e.remote_id);
          if (!cur || cur.isSelf) break;
          for (const p of e.points) cur.points.push({ x: p[0], y: p[1], pressure: p[2] });
          break;
        }
        case 'stroke:commit': {
          const captured = liveRef.current.get(e.remote_id) ?? null;
          liveRef.current.delete(e.remote_id);
          syncLiveCount();
          if (captured && captured.user_id !== selfUserId) {
            markFresh(e.stroke_id);
          }
          // v1 clients ALSO receive strokes:add with the full data (server dual-
          // emits), so the actual addStroke happens there. Here we only clear
          // the live preview entry.
          break;
        }
        case 'stroke:abort': {
          const cur = liveRef.current.get(e.remote_id);
          if (cur?.isSelf) break;
          liveRef.current.delete(e.remote_id);
          syncLiveCount();
          break;
        }
        case 'stroke:erase':
          eraseAndNotify(selfUserId, e.stroke_ids, e.by_user_id, removeStroke);
          useUndo.getState().pruneStrokes(e.stroke_ids);
          // 自己（含管理员身份）发起的擦除可被 Ctrl+Z 撤销
          if (e.by_user_id === selfUserId && e.ev_id != null) {
            useUndo.getState().push({ kind: 'erase', evId: e.ev_id });
          }
          break;
        case 'error':
          // eslint-disable-next-line no-console
          console.warn('ws error', e.code, e.reason);
          toastWsError(e.code, e.reason);
          break;
      }
    },
    [addStroke, removeStroke, selfUserId, setUsers, addUser, removeUser, setCursor, sizePxMap, syncLiveCount]
  );

  // Stash callbacks in refs so identity changes (e.g. onReopen rebuilt when
  // lastStrokeId updates after bulkLoad, sizePxMap rebuilt when theme loads)
  // don't tear down and reopen the WebSocket. Tearing down before the handshake
  // completes manifests as "WebSocket is closed before the connection is
  // established" and leaves the banner stuck on "正在连接画板".
  const handleEventRef = useRef(handleEvent);
  const onReopenRef = useRef(onReopen);
  const onStatusRef = useRef(onStatus);
  handleEventRef.current = handleEvent;
  onReopenRef.current = onReopen;
  onStatusRef.current = onStatus;

  useEffect(() => {
    if (!client) return;
    // New client == new board: the undo stack belongs to the previous board.
    useUndo.getState().clear();
    client.connect(
      (e) => handleEventRef.current(e),
      () => onReopenRef.current?.(),
      (s) => onStatusRef.current?.(s),
    );
    return () => client.close();
  }, [client]);

  const beginStroke = useCallback(
    (tool: Tool, color: string, sizeIdx: number, p: Point) => {
      if (selfUserId === null) return;
      const local_id = ++localIdRef.current;
      const remote_id = `${selfUserId}:${local_id}`;
      const sizePx = sizePxMap(sizeIdx, tool);
      liveRef.current.set(remote_id, {
        remote_id, user_id: selfUserId, tool, color, sizePx, sizeIdx,
        points: [p], isSelf: true, local_id, pendingBatch: [],
      });
      syncLiveCount();
      // v2: no network here — the whole stroke goes up on commit.
    },
    [selfUserId, sizePxMap, syncLiveCount]
  );

  const appendPoint = useCallback(
    (p: Point) => {
      // Hot path — mutates in place, zero React work per point.
      for (const s of liveRef.current.values()) {
        if (!s.isSelf) continue;
        s.points.push(p);
      }
    },
    []
  );

  const commitStroke = useCallback(() => {
    if (selfUserId === null) return;
    for (const [k, s] of liveRef.current) {
      if (s.isSelf && s.local_id !== undefined && client) {
        client.strokeCommitFull(s.local_id, s.tool, s.sizeIdx, s.color, s.points);
        liveRef.current.delete(k);
      }
    }
    syncLiveCount();
  }, [client, selfUserId, syncLiveCount]);

  const abortStroke = useCallback(() => {
    if (selfUserId === null) return;
    for (const [k, s] of liveRef.current) {
      if (s.isSelf) liveRef.current.delete(k);
    }
    syncLiveCount();
  }, [selfUserId, syncLiveCount]);

  /** Ctrl+Z：按最新动作分发 —— 撤销我最后画的一笔，或撤销我最后发起的擦除。 */
  const undo = useCallback(() => {
    if (!client) return;
    const a = useUndo.getState().pop();
    if (a === undefined) return;
    if (a.kind === 'stroke') client.erase([a.id]);
    else client.unerase(a.evId);
  }, [client]);

  return {
    liveRef, liveCount,
    beginStroke, appendPoint, commitStroke, abortStroke,
    undo, undoCount,
  };
}
